import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import {
	Contact,
	useGetDiscountProfileLazyQuery,
	useGetAllDiscountProfilesLazyQuery,
	DiscountProfile,
	DiscountProfileEntry,
} from "@repo/server-api";
import { OptionType, PaymentInformationType } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import * as Yup from "yup";

import Dropdown from "../sections/Dropdown";
import SectionHeader from "../SectionHeader";

const PaymentInformation = forwardRef(
	(
		props: Readonly<{
			contact?: Contact | null;
			setContact?: (value: Contact | null) => void;
			payment?: PaymentInformationType;
			setPayment?: (value: PaymentInformationType) => void;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const [info, setInfo] = useState<PaymentInformationType>({
			id: "",
			__typename: "PaymentConditionInformation",
			discountProfileId: {
				id: "",
				version: 0,
			},
			selectedEntryId: "",
			doesNotParticipateInVoucherProcess: false,
		});

		const [discountProfiles, setDiscountProfiles] = useState<DiscountProfile[]>([]);

		const [isLoaded, setIsLoaded] = useState<boolean>(false);
		const [discountProfile, setDiscountProfile] = useState<DiscountProfile | null>(null);

		const [optionElement, setOptionElement] = useState<OptionType | null>({
			title: "",
			inputValue: "",
			item: null,
		});

		const [getDiscountProfile] = useGetDiscountProfileLazyQuery({
			variables: { id: info.discountProfileId.id },
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data && data.node) {
					setDiscountProfile(data.node as DiscountProfile);
				}
			},
		});

		const [getDiscountProfiles] = useGetAllDiscountProfilesLazyQuery({
			variables: { first: 10, after: null },
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data) {
					if (data.allDiscountProfiles?.nodes) {
						setDiscountProfiles(
							data.allDiscountProfiles.nodes.map((profile) => ({
								...profile,
								isDeleted: false, // or appropriate default value
								version: 1, // or appropriate default value
							})),
						);
					}
				}
			},
		});

		useEffect(() => {
			if (isLoaded) {
				getDiscountProfiles();
			}
		}, [isLoaded]);

		useEffect(() => {
			if (props.contact && !isLoaded) {
				setIsLoaded(true);
				setInfo(
					props.contact.additionalInformation.filter(
						(info): info is PaymentInformationType =>
							(info as PaymentInformationType).__typename === "PaymentConditionInformation",
					)[0] ?? {
						id: "",
						__typename: "PaymentConditionInformation",
						discountProfileId: { id: "", version: 0 },
						selectedEntryId: "",
						doesNotParticipateInVoucherProcess: false,
					},
				);
			}
			if (props.payment && !isLoaded) {
				setIsLoaded(true);
				setInfo(props.payment);
			}
		}, [props.contact, props.payment, isLoaded]);

		const havePaymentInfo = (contact: Contact | null): boolean => {
			return contact
				? contact.additionalInformation.some(
						(i) => (i as PaymentInformationType).__typename === "PaymentConditionInformation",
					)
				: false;
		};

		useEffect(() => {
			if (props.setContact && props.contact) {
				props.setContact({
					...props.contact,
					id: props.contact.id ?? "",
					additionalInformation: havePaymentInfo(props.contact)
						? props.contact.additionalInformation.map((i) => {
								if ((i as PaymentInformationType).__typename === "PaymentConditionInformation") {
									return info;
								}
								return i;
							})
						: [...props.contact.additionalInformation, info],
				});
			}
			if (props.setPayment) {
				props.setPayment(info);
			}
			if (info.discountProfileId.id && discountProfile?.id !== info.discountProfileId.id) {
				getDiscountProfile();
			}
		}, [info]);

		useEffect(() => {
			if (discountProfile?.id) {
				setInfo({
					...info,
					discountProfileId: {
						...info.discountProfileId,
						id: discountProfile?.id,
					},
				});
			}
		}, [discountProfile]);

		const validationSchema = Yup.object().shape({
			// taxNumber: Yup.string()
			// 	.matches(/^[a-zA-Z0-9\-./\s]{5,20}$/, t("validation.taxNumber.invalid"))
			// 	.required(t("validation.taxNumber.required")),
			selectedEntryId: Yup.string().required(t("validation.selectedEntryId.required")),
		});

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(info, { abortEarly: false });
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
				}

				if (!discountProfile?.id)
					localErrors.discountProfileId = t("validation.discountProfileId.required");

				props.setErrors?.({ ...localErrors });
				return localErrors;
			}
		}, [props, discountProfile?.id, info, t, validationSchema]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		useEffect(() => {
			if (discountProfile) {
				setOptionElement({
					title: discountProfile.name,
					inputValue: discountProfile.name,
					item: discountProfile,
				});
			}
		}, [discountProfile]);

		return (
			<Stack spacing={3}>
				{/* Skonto Section */}
				<Stack spacing={2}>
					<SectionHeader title={t("cash-discount")} />
					<Dropdown
						doNotShowMenu={true}
						element={{
							title: optionElement?.title ?? "",
							inputValue: optionElement?.inputValue ?? "",
							item: optionElement?.item ?? null,
						}}
						setElement={(o) => {
							setOptionElement(o);
							if (o?.item && "entries" in o.item) {
								setDiscountProfile(o.item as DiscountProfile);
							} else {
								setDiscountProfile(null);
							}
						}}
						elements={discountProfiles}
						error={props.errors?.discountProfileId as string}
						setError={(value) => props.setErrors?.({ ...props.errors, discountProfileId: value })}
						searchLabel={t("discount-profiles")}
						noData={t("no-discount-profiles-found")}
					/>
					{!optionElement?.item || discountProfile?.entries.length === 0 ? (
						<Typography color="text.secondary">{t("no-entries")}</Typography>
					) : (
						<FormControl fullWidth>
							<InputLabel className={props.errors?.selectedEntryId ? "red" : ""} id="entries">
								{t("discount-profiles")}
							</InputLabel>
							<Select
								labelId="entries"
								value={info.selectedEntryId}
								label={t("discount-profiles")}
								onChange={(event) => {
									setInfo({
										...info,
										selectedEntryId: event.target.value ?? "",
									});
									props.setErrors?.({ ...props.errors, selectedEntryId: "" });
								}}
								error={!!props.errors?.selectedEntryId}>
								{discountProfile?.entries.map((entry: DiscountProfileEntry, index: number) => {
									return (
										<MenuItem key={"entry" + index} value={entry.id?.toString()}>
											{entry.discount + "% " + t("in") + " " + entry.dueDate + " " + t("days")}
										</MenuItem>
									);
								})}
							</Select>
							{props.errors?.selectedEntryId && (
								<FormHelperText className="red">
									{props.errors.selectedEntryId as string}
								</FormHelperText>
							)}
						</FormControl>
					)}
				</Stack>

				{/* Gutscheinverfahren Section */}
				<Stack spacing={2}>
					<SectionHeader title={t("voucher-process")} />
					<FormControlLabel
						control={
							<Switch
								checked={info.doesNotParticipateInVoucherProcess ?? false}
								onChange={(event) => {
									setInfo({
										...info,
										doesNotParticipateInVoucherProcess: event.target.checked,
									});
								}}
							/>
						}
						label={t("does-not-participate-in-voucher-process")}
					/>
				</Stack>
			</Stack>
		);
	},
);

PaymentInformation.displayName = "PaymentInformation";
export default PaymentInformation;
